import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { formatFeatureLabel, INTEGRATION_FEATURES } from '../../utils/companyFeatures.js';
import WhatsAppNotificationSettingsFields from './WhatsAppNotificationSettingsFields.js';
import EmailNotificationFields from './EmailNotificationFields.js';
import PaymentGatewayFields from './PaymentGatewayFields.js';
import EwaybillCredentialFields from './EwaybillCredentialFields.js';

const PANEL_META = {
  whatsapp_notification: {
    hint: 'Company Meta credentials plus sender / receiver / invoice options per event',
  },
  email_notification: {
    hint: 'Company SMTP for shipment, invoice, salary, daily summary',
  },
  payment_gateway: {
    hint: 'Razorpay UPI / Online QR for booking and payments',
  },
  ewaybill: {
    hint: 'PeriOne generate / update / cancel (company username, password, GSTIN)',
  },
  gst_verification: {
    hint: 'PeriOne GST search on shipment GST fields (uses shared API quota)',
  },
};

/**
 * Three integration toggles in one row; click opens/closes settings below.
 */
const CompanyIntegrationPanels = ({
  formData,
  onFeatureToggle,
  onChange,
  onWhatsappToggle,
  onWhatsappFieldChange,
  onWhatsappAccountChange,
  errors = {},
}) => {
  const [openKey, setOpenKey] = useState(null);

  const toggleOpen = (key) => {
    setOpenKey((prev) => (prev === key ? null : key));
  };

  const renderOpenContent = () => {
    if (!openKey) return null;

    if (openKey === 'whatsapp_notification') {
      if (!formData?.can_access?.whatsapp_notification) {
        return (
          <p className="text-xs sm:text-sm text-gray-500">
            Enable WhatsApp Notification above to configure this company Meta account and event options.
          </p>
        );
      }
      return (
        <WhatsAppNotificationSettingsFields
          formData={formData}
          onToggle={onWhatsappToggle}
          onFieldChange={onWhatsappFieldChange}
          onAccountChange={onWhatsappAccountChange}
          embedded
        />
      );
    }

    if (openKey === 'email_notification') {
      if (!formData?.can_access?.email_notification) {
        return (
          <p className="text-xs sm:text-sm text-gray-500">
            Enable Email Notification above to configure SMTP credentials.
          </p>
        );
      }
      return (
        <EmailNotificationFields
          formData={formData}
          onChange={onChange}
          errors={errors}
          embedded
        />
      );
    }

    if (openKey === 'payment_gateway') {
      if (!formData?.can_access?.payment_gateway) {
        return (
          <p className="text-xs sm:text-sm text-gray-500">
            Enable Payment Gateway above to configure Razorpay credentials.
          </p>
        );
      }
      return (
        <PaymentGatewayFields
          formData={formData}
          onChange={onChange}
          errors={errors}
          embedded
        />
      );
    }

    if (openKey === 'ewaybill') {
      if (!formData?.can_access?.ewaybill) {
        return (
          <p className="text-xs sm:text-sm text-gray-500">
            Enable E-Way Bill API to generate/update/cancel via PeriOne. When disabled, users enter e-way bill numbers manually on shipments.
          </p>
        );
      }
      return (
        <EwaybillCredentialFields
          formData={formData}
          onChange={onChange}
          embedded
        />
      );
    }

    if (openKey === 'gst_verification') {
      if (!formData?.can_access?.gst_verification) {
        return (
          <p className="text-xs sm:text-sm text-gray-500">
            Enable GST Verification to check consignor and consignee GSTIN against PeriOne after a full GST number is entered. Keep this off to avoid using GST API quota.
          </p>
        );
      }
      return (
        <p className="text-xs sm:text-sm text-gray-600">
          When a 15-character GST number is entered on Add or Edit Shipment, DigiQin looks it up once and shows whether it is Active. Turn this off anytime to stop GST API calls.
        </p>
      );
    }

    return null;
  };

  return (
    <div>
      <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
        Integrations
      </h3>
      <p className="text-xs sm:text-sm text-gray-500 mb-3">
        Enable each integration, then click the row item to expand its settings below.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2 sm:gap-3">
        {INTEGRATION_FEATURES.map((feature) => {
          const enabled = Boolean(formData?.can_access?.[feature]);
          const isOpen = openKey === feature;
          const meta = PANEL_META[feature] || {};

          return (
            <div
              key={feature}
              className={`border rounded-xl overflow-hidden ${
                isOpen ? 'border-orange-300 bg-orange-50/40' : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-start gap-2 p-3">
                <input
                  type="checkbox"
                  id={`integration-${feature}`}
                  checked={enabled}
                  onChange={() => {
                    onFeatureToggle(feature);
                    if (!enabled) setOpenKey(feature);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="w-4 h-4 mt-0.5 text-orange-600 focus:ring-blue-400 border-gray-300 rounded shrink-0"
                />
                <button
                  type="button"
                  onClick={() => toggleOpen(feature)}
                  className="flex-1 text-left min-w-0"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <span className="text-xs sm:text-sm font-medium text-gray-900">
                        {formatFeatureLabel(feature)}
                      </span>
                      <p className="text-xs text-gray-500 mt-0.5">{meta.hint}</p>
                      <p className={`text-xs mt-1 font-medium ${enabled ? 'text-green-700' : 'text-gray-500'}`}>
                        {enabled ? 'Enabled' : 'Disabled'}
                      </p>
                    </div>
                    <span className="text-gray-500 shrink-0 mt-0.5">
                      {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </span>
                  </div>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {openKey && (
        <div className="mt-3 border border-gray-200 rounded-xl p-3 sm:p-4 bg-white">
          {renderOpenContent()}
        </div>
      )}
    </div>
  );
};

export default CompanyIntegrationPanels;
